import { Bot, Mic } from "lucide-react";
import { useState } from "react";

interface VoiceUIProps {
  onClose: () => void;
}

export function VoiceUI({ onClose }: VoiceUIProps) {
  const [isListening, setIsListening] = useState(false);

  const toggleListening = () => {
    setIsListening(!isListening);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full bg-background/95 text-foreground p-4 animate-fade-in">
      <div className="relative">
        {/* Outer glow */}
        <div 
          className={`absolute inset-[-30px] bg-primary/10 rounded-full blur-2xl transition-transform duration-1000 ${
            isListening ? 'scale-150 animate-pulse' : 'scale-100'
          }`}
        />
        
        {/* Middle glow */}
        <div 
          className={`absolute inset-[-15px] bg-primary/20 rounded-full blur-xl transition-transform duration-700 ${
            isListening ? 'scale-125 animate-pulse' : 'scale-100'
          }`}
        />
        
        {/* Inner glow */}
        <div 
          className={`absolute inset-[-5px] bg-primary/30 rounded-full blur-md transition-transform duration-500 ${
            isListening ? 'scale-110 animate-pulse' : 'scale-100'
          }`}
        />
        
        {/* Avatar container with click interaction */}
        <button
          onClick={toggleListening}
          className={`relative h-28 w-28 rounded-full bg-primary/5 backdrop-blur-sm 
            flex items-center justify-center border-2 border-primary/50 
            transition-all duration-300 hover:scale-105
            ${isListening ? 'border-primary animate-pulse shadow-lg shadow-primary/20' : ''}
          `}
        >
          {isListening ? (
            <Bot className="h-14 w-14 text-primary animate-fade-in" />
          ) : (
            <Mic className="h-14 w-14 text-primary/70 animate-fade-in" />
          )}
        </button>
      </div>
      
      <div className="mt-8 space-y-2 text-center animate-fade-in">
        <p className="text-xl font-medium">
          {isListening ? "I'm listening..." : "Tap the mic to start"}
        </p>
        <p className="text-sm text-muted-foreground">
          {isListening 
            ? "Speak naturally, I'm here to help" 
            : "Click the microphone icon when you're ready"}
        </p>
        
        <button
          onClick={onClose}
          className="mt-4 px-4 py-1.5 rounded-full bg-primary/10 hover:bg-primary/20 
            transition-colors duration-300 text-xs font-medium
            hover:shadow-md hover:scale-105 active:scale-95"
          aria-label="Close Voice Mode"
        >
          Close Voice Mode
        </button>
      </div>
    </div>
  );
}
